import type { SEOHandle } from "@nasa-gcn/remix-seo";
import type { MetaFunction } from "@remix-run/node";

import { CourseRow } from "~/components/courses/CourseCard";
import CtaBanner from "~/components/sections/CtaBanner";
import Faq from "~/components/sections/Faq";
import Testimonial from "~/components/sections/Testimonial";
import { useBooking } from "~/context/BookingContext";
import { COURSES } from "~/data/courses";
import { HIGH_FETCH_PRIORITY } from "~/helpers/imagePriority";
import generateSitemapEntriesFor from "~/helpers/sitemapEntriesWithLocal";
import common from "~/i18n/common";
import faqDict from "~/i18n/faq";
import pages from "~/i18n/pages";
import { useT } from "~/i18n/useT";
import { buildMeta, langFromParams } from "~/seo/meta";
import { breadcrumbSchema, faqSchema } from "~/seo/schema";

import "./courses.scss";

export const meta: MetaFunction = ({ params }) => {
  const lang = langFromParams(params);
  const t = pages[lang].coursesPage;

  return [
    ...buildMeta({
      lang,
      path: "/courses",
      title: t.metaTitle,
      description: t.metaDescription,
    }),
    { "script:ld+json": faqSchema(faqDict[lang].items) },
    {
      "script:ld+json": breadcrumbSchema(lang, [
        { name: common[lang].a11y.home, path: "/" },
        { name: common[lang].nav.courses, path: "/courses" },
      ]),
    },
  ];
};

export const handle: SEOHandle = generateSitemapEntriesFor(
  "/courses",
  undefined,
  "weekly",
  0.9,
);

export default function CoursesPage() {
  const p = useT(pages);
  const t = p.coursesPage;
  const c = useT(common);
  const booking = useBooking();

  return (
    <main id="main-content">
      <section className="ss-container ss-courses-hero">
        <div>
          <p className="ss-eyebrow ss-eyebrow--blue">{t.eyebrow}</p>
          <h1 className="ss-h1 ss-courses-hero__title">{t.title}</h1>
          <p className="ss-lead ss-courses-hero__lead">{t.lead}</p>

          <dl className="ss-courses-hero__stats">
            <div>
              <dd className="ss-courses-hero__stat-value">
                {t.statCoursesValue}
              </dd>
              <dt className="ss-courses-hero__stat-label">
                {t.statCoursesLabel}
              </dt>
            </div>
            <span className="ss-courses-hero__divider" aria-hidden="true" />
            <div>
              <dd className="ss-courses-hero__stat-value">
                {t.statYearsValue}
              </dd>
              <dt className="ss-courses-hero__stat-label">
                {t.statYearsLabel}
              </dt>
            </div>
          </dl>

          <button
            type="button"
            className="ss-btn ss-btn--primary"
            onClick={() => booking.open()}
          >
            {c.cta.scheduleConsultation}
          </button>
        </div>

        <div className="ss-courses-hero__media">
          <img
            src="/img/hero/hero.webp"
            alt={t.heroImageAlt}
            width={560}
            height={460}
            loading="eager"
            {...HIGH_FETCH_PRIORITY}
            decoding="async"
          />
        </div>
      </section>

      <section className="ss-section ss-container">
        <h2 className="ss-h2 ss-courses__heading">{t.coreTitle}</h2>
        <div className="ss-courses__list">
          {COURSES.map((course) => (
            <CourseRow key={course.slug} course={course} />
          ))}
        </div>
      </section>

      <Testimonial />
      <Faq />

      <CtaBanner
        title={p.home.cta.title}
        body={p.home.cta.body}
        action={c.cta.scheduleConsultation}
      />
    </main>
  );
}
